import { Box, Divider, Group } from '@byecode/ui'
import type { ArgumentsSchema, EndArg, SubProcessArg } from '@lighthouse/core'
import type {
    ClickTriggerConfigure,
    EndConfigure,
    FlowNode,
    HandleSubProcessActionConfig,
    VariableTriggerConfigure
} from '@lighthouse/shared'
import { ListItem4ByecodeUi, OUTPUT_SINGLE_DATA_NODES } from '@lighthouse/shared'
import { useAtomValue } from 'jotai'
import { find } from 'rambda'
import React, { useMemo } from 'react'

import { workflowListAtom } from '@/atoms/workflow/state'

import OutputVariable from '../../../OutputVariable'
import * as SC from '../../styles'
import { SettingSubProcessContent } from '../component/SettingSubProcessContent'
import { SubProcessSelector } from '../component/SubProcessSelector'

interface HandleSubProcessActionConfigureProps {
    nodeType?: string
    allParentNodes: FlowNode[]
    actionTypeSwitcher?: React.ReactNode
    value: HandleSubProcessActionConfig
    onChange: (value: HandleSubProcessActionConfig) => void
}

export const HandleSubProcessActionConfigure: React.FC<HandleSubProcessActionConfigureProps> = ({
    allParentNodes,
    actionTypeSwitcher,
    value,
    onChange
}) => {
    const workflows = useAtomValue(workflowListAtom)
    const { subProcessId, args } = value

    const allSingeParentNode = useMemo(
        () => allParentNodes.filter(node => OUTPUT_SINGLE_DATA_NODES.has(node.data.nodeType)),
        [allParentNodes]
    )

    const [subProcessArgs, subProcessOutputArgs]: [SubProcessArg[], EndArg[]] = useMemo(() => {
        let subProcessArgs: ArgumentsSchema[] = []
        let subProcessOutputArgs: EndArg[] = []
        const workflow = find(item => item.id === subProcessId && item.type === 'subProcess', workflows)
        if (!workflow) {
            return [subProcessArgs, subProcessOutputArgs]
        }
        const nodeList = workflow.content?.nodes || []
        const variableNode = find(item => item.data.nodeType === 'ARG_TRIGGER', nodeList)
        const endNode = find(item => item.data.nodeType === 'END', nodeList)
        if (variableNode) {
            const { config } = variableNode.data as ClickTriggerConfigure | VariableTriggerConfigure
            if (config && config?.args) {
                subProcessArgs = config.args
            }
        }
        if (endNode) {
            const { config } = endNode.data as EndConfigure
            if (config && config?.outputArgs) {
                subProcessOutputArgs = config.outputArgs
            }
        }
        return [subProcessArgs, subProcessOutputArgs]
    }, [subProcessId, workflows])

    const content = useMemo(() => {
        if (subProcessId && subProcessArgs && subProcessArgs.length > 0) {
            const { args } = value
            const list = args || []
            const data =
                subProcessArgs.map(item => {
                    const dataItem = find(d => d.id === item.id, list)
                    return {
                        ...item,
                        value: dataItem?.value
                    }
                }) || []
            return (
                <>
                    <Divider color="var(--color-gray-200)" />
                    <Group label="子流程参数值设置">
                        <Box px={8}>
                            <SC.Tip>设置子流程的参数值，参数值将会传递到子流程中。</SC.Tip>
                            <SettingSubProcessContent
                                data={data}
                                parentNodes={allSingeParentNode}
                                allParentNodes={allParentNodes}
                                onChange={args => onChange({ ...value, args })}
                            />
                        </Box>
                    </Group>
                    <Divider color="var(--color-gray-200)" />
                    <OutputVariable options={subProcessOutputArgs} />
                </>
            )
        }
    }, [allParentNodes, allSingeParentNode, onChange, subProcessArgs, subProcessId, subProcessOutputArgs, value])

    return (
        <SC.Container>
            {actionTypeSwitcher && (
                <Group label="动作配置">
                    <ListItem4ByecodeUi>
                        <SC.FormItemLabelWrapper>
                            <SC.FormItemLabel>类型</SC.FormItemLabel>
                        </SC.FormItemLabelWrapper>
                        <SC.FormItemContent>{actionTypeSwitcher}</SC.FormItemContent>
                    </ListItem4ByecodeUi>
                    <SubProcessSelector
                        workflows={workflows}
                        value={subProcessId}
                        onChange={val => onChange({ ...value, subProcessId: val })}
                    />
                </Group>
            )}
            <Group label="子流程">
                <SubProcessSelector
                    workflows={workflows}
                    value={subProcessId}
                    onChange={val => onChange({ ...value, subProcessId: val, args: [] })}
                />
            </Group>
            {content}
        </SC.Container>
    )
}
